import { translate, currentLocale } from '../i18n';
const API_BASE = '/api/v1';

export function getToken(): string | null {
  return localStorage.getItem('rsvs.access');
}

export function getTenantSlug(): string | null {
  return localStorage.getItem('rsvs.tenant_slug');
}

export function clearAuth() {
  localStorage.removeItem('rsvs.access');
  localStorage.removeItem('rsvs.refresh');
  localStorage.removeItem('rsvs.user');
  localStorage.removeItem('rsvs.tenant_slug');
  localStorage.removeItem('rsvs.tenant_name');
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export interface ApiError {
  message: string;
  statusCode?: number;
}

export class FetchError extends Error {
  statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.statusCode = statusCode;
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('rsvs.refresh');
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      const newAccess = data.access_token ?? data.accessToken;
      const newRefresh = data.refresh_token ?? data.refreshToken;
      if (newAccess) localStorage.setItem('rsvs.access', newAccess);
      if (newRefresh) localStorage.setItem('rsvs.refresh', newRefresh);
      return Boolean(newAccess);
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const tenantSlug = getTenantSlug();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      clearAuth();
      window.location.href = '/login';
      throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401);
    }
    // Retry with new token
    const newToken = getToken();
    if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
    const retry = await fetch(`${API_BASE}${path}`, { ...options, headers });
    if (retry.status === 401) {
      clearAuth();
      window.location.href = '/login';
      throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401);
    }
    if (!retry.ok) {
      let message = translate(currentLocale(), 'd-labels.err.http', { status: retry.status });
      try { const body = await retry.json(); message = body.message || message; } catch { /* ignore */ }
      throw new FetchError(message, retry.status);
    }
    if (retry.status === 204) return undefined as T;
    return retry.json() as Promise<T>;
  }

  if (!res.ok) {
    let message = translate(currentLocale(), 'd-labels.err.http', { status: res.status });
    try {
      const body = await res.json();
      message = body.message || message;
    } catch {
      // ignore
    }
    throw new FetchError(message, res.status);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export async function apiFetchForm<T = unknown>(
  path: string,
  form: FormData,
): Promise<T> {
  const tryOnce = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenantSlug = getTenantSlug();
    if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;
    return fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: form });
  };

  let res = await tryOnce(getToken());

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      clearAuth();
      window.location.href = '/login';
      throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401);
    }
    res = await tryOnce(getToken());
    if (res.status === 401) {
      clearAuth();
      window.location.href = '/login';
      throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401);
    }
  }

  if (!res.ok) {
    let message = translate(currentLocale(), 'd-labels.err.http', { status: res.status });
    try { const body = await res.json(); message = body.message || message; } catch { /* ignore */ }
    throw new FetchError(message, res.status);
  }

  return res.json() as Promise<T>;
}

export async function apiFetchBlob(path: string): Promise<Blob> {
  const token = getToken();
  const tenantSlug = getTenantSlug();
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantSlug) headers['X-Tenant-Slug'] = tenantSlug;

  let res = await fetch(`${API_BASE}${path}`, { headers });

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (!refreshed) { clearAuth(); window.location.href = '/login'; throw new FetchError(translate(currentLocale(), 'd-labels.err.unauthorized'), 401); }
    const newToken = getToken();
    if (newToken) headers['Authorization'] = `Bearer ${newToken}`;
    res = await fetch(`${API_BASE}${path}`, { headers });
  }

  if (!res.ok) throw new FetchError(translate(currentLocale(), 'd-labels.err.http', { status: res.status }), res.status);
  return res.blob();
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
}
